import React, { useEffect, useRef, useState } from 'react';
import type { MailersHeader, MailersTemplate } from 'bundles/connect/types';
import { Button, Card, Col, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap-5';
import { Editor } from '@tiptap/core';

import MailersTemplatesHeader from './mailers_templates_header';
import TiptapEditor, { TEXT_EDITOR_DOM_ID } from '../../rich_text_editor/tiptap_editor';
import { faEye, faSave, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { displayToast } from 'bundles/connect/helpers/display_toast';
import {
  CALL_LISTS_SHOW,
  callListUrl,
  deleteMailersTemplates,
  mailerErrorMessage,
  mailersPreviewUrl,
  updateMailersTemplate
} from 'bundles/connect/helpers/prospect_api';
import { showSnackbar } from 'components/ui/snackbar/helper';
import MailersTemplatesLiquid from './mailers_templates_liquid';
import { initializeEditor } from '../../rich_text_editor/helpers';
import MailersHeaderEditModal from '../header/mailers_header_edit';
import ReactDOM from 'react-dom';
import MailersHeaderPreview from '../header/mailers_header_preview';

type MailersTempaltesEditProps = {
  template: MailersTemplate;
  initialHeader: MailersHeader;
  callListId: number;
}

export default function MailersTemplatesEdit({ template, initialHeader, callListId }: MailersTempaltesEditProps) {
  const [title, setTitle] = useState(template.title);
  const [body, setBody] = useState(template.body);
  const [currentFontSize, setCurrentFontSize] = useState('16px');
  const [showLetterheadModal, setShowLetterheadModal] = useState(false);
  const [header, setHeader] = useState(initialHeader);
  const editorRef = useRef(null);
  const tiptapInstance = useRef<Editor | null>(null);

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      deleteMailersTemplates([template.id]).then(() => {
        displayToast('Template deleted successfully', 'success');
        window.location.href = `${callListUrl(CALL_LISTS_SHOW, callListId)}/mailers_templates`;
      });
    }
  };

  const handleSave = () => {
    updateMailersTemplate(callListId, template.id, { title, body }).then(() => {
      displayToast('Template updated successfully', 'success');
    }).catch((err: unknown) => {
      showSnackbar({ text: mailerErrorMessage(err), variant: 'danger' });
    });
  };

  const handlePreview = () => {
    updateMailersTemplate(callListId, template.id, { title, body }).then(() => {
      setTimeout(() => window.location.href = mailersPreviewUrl(callListId, template.id), 1000);
    }).catch((err: unknown) => {
      showSnackbar({ text: mailerErrorMessage(err), variant: 'danger' });
    });
  };

  const addLiquidToBody = (liquid: string) => {
    const editor = tiptapInstance.current;
    if (editor) {
      const { from, to } = editor.state.selection;
      editor.chain().focus().insertContentAt({ from, to }, liquid).run();
    }
  };

  useEffect(() => {
    if (editorRef.current && !tiptapInstance.current) {
      initializeEditor(
        editorRef,
        tiptapInstance,
        body,
        setBody,
        setCurrentFontSize
      );
    }
  }, []);

  useEffect(() => {
    const rte = document.getElementById(TEXT_EDITOR_DOM_ID);

    if (rte) {
      void document.getElementById('header-edit-container')?.remove();
      const headerEditContainer = document.createElement('div');
      headerEditContainer.id = 'header-edit-container';
      ReactDOM.render(
        <MailersHeaderPreview edit initialHeader={header} setShow={setShowLetterheadModal} />,
        headerEditContainer
      );
      rte.insertBefore(headerEditContainer, rte.firstChild);
    }
  }, [header]);

  return (
    <div className="position-absolute w-100 h-100 d-flex flex-column">
      <MailersTemplatesHeader
        callListId={callListId}
        edit
        setTemplateTitle={setTitle}
        templateTitle={title}
      />
      <div className="flex-grow-1 position-relative">
        <Container className="position-relative h-100 w-100 p-6" fluid="xl">
          <div className="h-100 w-100 position-relative">
            <Card className="position-absolute w-100 d-flex flex-column h-100 overflow-hidden shadow">
              <Row className="flex-grow-1 overflow-hidden g-0">
                <Col className="h-100 py-4 ps-4 pe-2" xs={8}>
                  <div className="d-flex h-100">
                    <TiptapEditor
                      editorRef={editorRef}
                      extraClasses="bg-body flex-grow-1"
                      parentFontSize={currentFontSize}
                      tiptapInstance={tiptapInstance}
                    />
                  </div>
                </Col>
                <Col className="h-100 ps-2 pe-4">
                  <MailersTemplatesLiquid updateBody={addLiquidToBody} />
                </Col>
              </Row>
              <Card.Footer className="flex-shrink-0 d-flex justify-content-end gap-4 align-items-center">
                <OverlayTrigger
                  overlay={<Tooltip>Delete Template</Tooltip>}
                  placement="left"
                >
                  <Button
                    as="a"
                    className="squared p-1"
                    variant="text-danger"
                    onClick={handleDelete}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </OverlayTrigger>
                <Button
                  variant="outline"
                  onClick={handleSave}
                >
                  <FontAwesomeIcon icon={faSave} />
                  Save Template
                </Button>
                <Button
                  variant="primary"
                  onClick={handlePreview}
                >
                  <FontAwesomeIcon icon={faEye} />
                  Save & Preview
                </Button>
              </Card.Footer>
            </Card>
          </div>
        </Container>
      </div>
      <MailersHeaderEditModal
        header={header}
        setHeader={setHeader}
        setShow={setShowLetterheadModal}
        show={showLetterheadModal}
      />
    </div>
  );
}
